/**
 * One way to submit a form: validate, lock the buttons, call the API, and put server-side field
 * errors next to the controls they belong to. Anything else goes to `onError` (default: a toast).
 */
import { $$ } from '../core/dom.js';
import { ApiError, reportError } from '../core/errors.js';
import { clearFieldErrors, readForm, showFieldErrors, validate } from './forms.js';

/**
 * @param {HTMLFormElement} form
 * @param {(values: object) => Promise<any>} work  called with readForm(form) once validation passes
 * @param {{onError?: (error: Error) => void, check?: (values: object) => string|null}} [options]
 * @returns {Promise<{ok: true, value: any}|{ok: false}>}
 */
export async function submitForm(form, work, { onError, check } = {}) {
  clearFieldErrors(form);
  if (!validate(form)) return { ok: false };
  const values = readForm(form);
  const problem = check?.(values);
  if (problem) {
    onError?.(new Error(problem));
    return { ok: false };
  }
  const buttons = $$('button:not([type=button])', form);
  buttons.forEach((b) => {
    b.disabled = true;
    b.setAttribute('aria-busy', 'true');
  });
  try {
    const value = await work(values);
    return { ok: true, value };
  } catch (error) {
    if (error instanceof ApiError && error.status === 422 && showFieldErrors(form, error.fieldErrors)) return { ok: false };
    if (onError) onError(error);
    else reportError(error);
    return { ok: false };
  } finally {
    buttons.forEach((b) => {
      if (b.isConnected) {
        b.disabled = false;
        b.removeAttribute('aria-busy');
      }
    });
  }
}
